import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus, ChevronUp, ChevronDown, ShoppingCart } from "lucide-react";
import { brl } from "@/api";

/**
 * VariantPicker — modal padronizado para seleção de variantes/sabores.
 *
 * Props:
 *  - product: objeto com {name, price, promo_price, photos, allow_oversell, variants:[{name, price, cost, quantity, photo}], variants_label}
 *  - initialQuantities: { [variantName]: quantity } — quantidades já no carrinho/pedido
 *  - confirmLabel: texto do botão final (ex: "Continuar venda", "Adicionar ao carrinho")
 *  - onConfirm: (selections: [{variant_name, quantity, unit_price, unit_cost?, photo}]) => void
 *  - onClose: () => void
 *  - showCost: bool — se true, mostra cost (somente uso interno do Vendas)
 */
export default function VariantPicker({
  product,
  initialQuantities = {},
  confirmLabel = "Adicionar ao carrinho",
  onConfirm,
  onClose,
  showCost = false,
}) {
  const [open, setOpen] = useState(true);
  // quantities por nome da variante
  const [qty, setQty] = useState({ ...initialQuantities });
  const [section, setSection] = useState(true); // collapsible header

  const label = product?.variants_label || "Variações";
  const variants = useMemo(() => product?.variants || [], [product]);

  const total = useMemo(() => variants.reduce((s, v) => {
    const q = Number(qty[v.name] || 0);
    if (q <= 0) return s;
    const price = Number(v.price ?? product.promo_price ?? product.price ?? 0);
    return s + price * q;
  }, 0), [qty, variants, product]);

  const itemsCount = useMemo(() => variants.reduce((s, v) => s + Math.max(0, Number(qty[v.name] || 0)), 0), [qty, variants]);

  const inc = (v) => {
    const cur = Number(qty[v.name] || 0);
    const max = product.allow_oversell ? Infinity : Number(v.quantity || 0);
    if (cur >= max) return;
    setQty(prev => ({ ...prev, [v.name]: cur + 1 }));
  };
  const dec = (v) => {
    const cur = Number(qty[v.name] || 0);
    if (cur <= 0) return;
    setQty(prev => ({ ...prev, [v.name]: cur - 1 }));
  };
  const setExact = (v, val) => {
    const max = product.allow_oversell ? Infinity : Number(v.quantity || 0);
    const n = Math.max(0, Math.min(max, Number(val) || 0));
    setQty(prev => ({ ...prev, [v.name]: n }));
  };

  const handleConfirm = () => {
    const selections = variants
      .filter(v => Number(qty[v.name] || 0) > 0)
      .map(v => ({
        variant_name: v.name,
        quantity: Number(qty[v.name]),
        unit_price: Number(v.price ?? product.promo_price ?? product.price ?? 0),
        unit_cost: showCost ? Number(v.cost ?? product.cost ?? 0) : undefined,
        photo: v.photo || null,
      }));
    onConfirm(selections);
    setOpen(false);
    onClose && onClose();
  };

  const handleClose = () => {
    setOpen(false);
    onClose && onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-lg sm:rounded-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="variant-picker-dialog"
      >
        <DialogHeader className="px-5 py-4 border-b border-[#E5E5E5] dark:border-white/10">
          <DialogTitle className="text-base font-display tracking-tight" data-testid="variant-picker-title">
            Opções <span className="font-normal text-neutral-600 dark:text-neutral-300">{product.name}</span>{" "}
            <span className="text-neutral-400 text-sm">(Selecionar {label.toLowerCase()})</span>
          </DialogTitle>
        </DialogHeader>

        {/* ── HEADER de seção colapsável ── */}
        <button
          type="button"
          onClick={() => setSection(s => !s)}
          className="w-full flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5 transition"
        >
          <span className="font-display text-base">{label}</span>
          {section ? <ChevronUp size={16} className="text-neutral-400"/> : <ChevronDown size={16} className="text-neutral-400"/>}
        </button>

        {/* ── Lista de variantes ── */}
        {section && (
          <div className="flex-1 overflow-y-auto divide-y divide-[#F0F0F0] dark:divide-white/5" data-testid="variant-picker-list">
            {variants.map(v => {
              const stockNum = Number(v.quantity || 0);
              const productPrice = Number(product.promo_price ?? product.price ?? 0);
              const variantPrice = Number(v.price ?? productPrice);
              const hasPromo = v.price != null && productPrice > variantPrice && productPrice > 0;
              const q = Number(qty[v.name] || 0);
              const photo = v.photo || product.photos?.[0] || null;
              const out = !product.allow_oversell && stockNum <= 0;

              return (
                <div
                  key={v.name}
                  className={`flex items-stretch gap-3 p-4 ${out ? "opacity-50" : ""}`}
                  data-testid={`variant-row-${v.name}`}
                >
                  {/* Thumbnail */}
                  <button
                    type="button"
                    onClick={() => !out && inc(v)}
                    disabled={out}
                    className="relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-neutral-100 dark:bg-white/5 border border-[#E5E5E5] dark:border-white/10"
                  >
                    {photo
                      ? <img src={photo} alt={v.name} className="w-full h-full object-cover"/>
                      : <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">sem foto</div>}
                    {q > 0 && (
                      <div className="absolute top-0 left-0 bg-emerald-500 text-white text-xs font-display font-bold w-7 h-7 flex items-center justify-center rounded-br-md" data-testid={`variant-badge-${v.name}`}>
                        {q}
                      </div>
                    )}
                  </button>

                  {/* Nome */}
                  <button
                    type="button"
                    onClick={() => !out && inc(v)}
                    disabled={out}
                    className="flex-1 text-left min-w-0 disabled:cursor-not-allowed"
                  >
                    <p className="font-display text-base tracking-tight truncate">{v.name}</p>
                    {out && <p className="text-xs text-red-600 mt-0.5">Esgotada</p>}
                  </button>

                  {/* Preço + Estoque + Stepper */}
                  <div className="flex flex-col items-end justify-between">
                    <div className="text-right">
                      {hasPromo && <div className="text-xs text-neutral-400 line-through">{brl(productPrice)}</div>}
                      <div className="text-sm font-medium">{brl(variantPrice)}</div>
                      <div className="text-xs text-neutral-500">Estoque: {stockNum}</div>
                    </div>
                    {q > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <button onClick={()=>dec(v)} className="h-6 w-6 rounded border border-[#E5E5E5] dark:border-white/10 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-white/5" data-testid={`variant-dec-${v.name}`}>
                          <Minus size={12}/>
                        </button>
                        <Input
                          value={q}
                          onChange={(e)=>setExact(v, e.target.value)}
                          className="h-6 w-12 text-center text-sm px-1"
                          data-testid={`variant-qty-${v.name}`}
                        />
                        <button onClick={()=>inc(v)} disabled={out || (!product.allow_oversell && q >= stockNum)} className="h-6 w-6 rounded border border-[#E5E5E5] dark:border-white/10 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-white/5 disabled:opacity-40" data-testid={`variant-inc-${v.name}`}>
                          <Plus size={12}/>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {variants.length === 0 && (
              <div className="p-6 text-center text-sm text-neutral-400">Sem {label.toLowerCase()} cadastradas.</div>
            )}
          </div>
        )}

        {/* ── Footer com total + ação ── */}
        <div className="border-t border-[#E5E5E5] dark:border-white/10 px-5 py-3 flex items-center gap-3 bg-white dark:bg-[#0A0F19]">
          <div className="text-right">
            <p className="font-display text-xl tracking-tight" data-testid="variant-picker-total">{brl(total)}</p>
            <p className="text-[11px] text-neutral-500">{itemsCount} {itemsCount === 1 ? "item" : "itens"}</p>
          </div>
          <Button
            onClick={handleConfirm}
            disabled={itemsCount === 0}
            className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white text-base border-2 border-emerald-400 disabled:opacity-50"
            data-testid="variant-picker-confirm"
          >
            <ShoppingCart size={16} className="mr-2"/> {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
